import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import { getExample } from 'awesome-phonenumber';
import cx from 'classnames';
import { Input } from '@mc/wink';
import type { InputProps } from '@mc/wink/components/Input';
import { useTranslateMessage } from '@mc/internationalization/translationUtils';
import { CountrySelector } from './CountrySelector';
import { getCountryCodeByName } from './input-phone-helpers';
import { validatePhoneNumber, getMaxAllowedLength } from './phone-validation';
import { extractNumbers } from './phone-utils';
import stylesheet from './InputPhone.css';

// Type definitions
type CountryOption = {
  value: string;
  label: string;
  code: string;
  flag: string;
};

export type InputPhoneProps = Omit<
  InputProps,
  'onChange' | 'phoneCountrySelector'
> & {
  // Phone-specific props
  country?: string;
  enableLengthRestriction?: boolean;
  enableValidation?: boolean;
  onCountryChange?: (country: CountryOption) => void;
  restrictToNumbers?: boolean;
  selectedCountry?: CountryOption;
  showCountrySelector?: boolean;
  // Override onChange to match phone input expectations
  onChange?: (value: string) => void;
};

export const InputPhone = ({
  type = 'tel',
  value = '',
  onChange = () => {},
  onCountryChange = () => {},
  selectedCountry = {
    value: 'us',
    label: 'United States',
    code: '+1',
    flag: '🇺🇸',
  },
  showCountrySelector = true,
  disabled = false,
  readOnly = false,
  error = '',
  placeholder,
  id,
  enableValidation = false,
  restrictToNumbers = true,
  enableLengthRestriction = true, // New prop to enable length limiting
  ...props
}: InputPhoneProps) => {
  const phoneNumberLabel = useTranslateMessage({
    id: 'mcds.input_phone.label',
    defaultMessage: 'Phone number*',
    description: 'Label for phone number input field',
    values: null,
  });
  const phoneNumberIsRequired = useTranslateMessage({
    id: 'mcds.input_phone.phone_number_is_required',
    defaultMessage: 'Phone number is required',
    description: 'Error message when phone number is required but not provided',
    values: null,
  });
  const invalidPhone = useTranslateMessage({
    id: 'mcds.input_phone.invalid_phone',
    defaultMessage: 'Enter a valid phone number',
    description: 'Error message when phone number format is invalid',
    values: null,
  });
  const [validationError, setValidationError] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isCountrySelectorFocused, setIsCountrySelectorFocused] =
    useState(false);

  // Refs for focus management
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const countrySelectorRef = useRef<HTMLDivElement>(null);

  // Generate a unique ID for the input if one isn't provided
  const inputId = useMemo(() => {
    return id || `phone-input-${Math.random().toString(36).substr(2, 9)}`;
  }, [id]);

  // Manage internal country state - initialize with selectedCountry but allow independent updates
  const [internalCountry, setInternalCountry] = useState(selectedCountry);

  // Sync internal state with selectedCountry prop changes
  useEffect(() => {
    setInternalCountry(selectedCountry);
  }, [selectedCountry]);

  // Use internal country when available, fall back to selectedCountry prop
  const currentCountry = internalCountry || selectedCountry;

  // Click-outside functionality to close country selector dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isDropdownOpen) {
        return;
      }

      const target = event.target as Node;

      // Check if click is on our container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // Check if click is on the dropdown popup or its options
      const dropdownPopup = document.querySelector('[role="listbox"]');
      if (dropdownPopup && dropdownPopup.contains(target)) {
        return;
      }

      // Check if click is on any dropdown-related element
      const dropdownContainer = document.querySelector('[data-popup="true"]');
      if (dropdownContainer && dropdownContainer.contains(target)) {
        return;
      }

      // Check if click is on any input within dropdown (search, filter, etc.)
      const isInputElement = (target as Element).tagName === 'INPUT';
      if (isInputElement) {
        // Check if this input is within a dropdown container
        const isInDropdown = (target as Element).closest(
          '[role="listbox"], .wink-select-dropdown, [data-popup="true"], .popup',
        );
        if (isInDropdown) {
          return;
        }
      }

      // Check if click is on search input within dropdown - multiple selectors for safety
      const searchInput = (target as Element).closest(
        'input[type="search"], input[placeholder*="search"], input[placeholder*="Search"], .wink-select-search input, input[aria-label*="search"], input[aria-label*="Search"]',
      );
      if (searchInput) {
        return;
      }

      // Check if click is on any country option
      const countryOption = (target as Element).closest('[role="option"]');
      if (countryOption) {
        return;
      }

      // Check if click is on any SelectInline dropdown element
      const selectDropdown = (target as Element).closest(
        '.wink-select-dropdown, [class*="listbox"], [class*="popup"]',
      );
      if (selectDropdown) {
        return;
      }

      // Click is truly outside - close the dropdown
      if (countrySelectorRef.current) {
        const combobox = countrySelectorRef.current.querySelector(
          '[role="combobox"]',
        ) as HTMLElement;
        if (combobox) {
          // Dispatch Escape key event to close dropdown
          const escapeEvent = new KeyboardEvent('keydown', {
            key: 'Escape',
            code: 'Escape',
            bubbles: true,
            cancelable: true,
          });
          combobox.dispatchEvent(escapeEvent);

          // Update our state
          setIsDropdownOpen(false);
        }
      }
    };

    // Use click instead of mousedown to avoid interfering with focus/input events
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Phone number validation function
  const validatePhone = useCallback(
    (phoneValue: string) => {
      const countryCode = currentCountry?.value || null;
      const dialingCode = currentCountry?.code || null;

      return validatePhoneNumber(
        phoneValue,
        phoneNumberIsRequired,
        invalidPhone,
        countryCode,
        dialingCode,
      );
    },
    [phoneNumberIsRequired, invalidPhone, currentCountry],
  );

  const getNumberFormat = useCallback(() => {
    if (!currentCountry) {
      return '555 867 5309';
    }

    let countryCode = null;
    if (currentCountry.value && currentCountry.value.length === 2) {
      countryCode = currentCountry.value.toUpperCase();
    } else if (currentCountry.label) {
      countryCode = getCountryCodeByName(currentCountry.label);
    }

    if (countryCode) {
      try {
        const example = getExample(countryCode);
        if (example && example.number && example.number.international) {
          // Remove the dialing code from the example since it will be shown separately
          return example.number.international.replace(/^\+\d+\s*/, '');
        }
      } catch (getExampleError) {
        // Silently fall back to default placeholder if example lookup fails
      }
    }

    return '555 867 5309';
  }, [currentCountry]);

  const dynamicPlaceholder = useMemo(() => {
    if (placeholder) {
      return placeholder;
    }

    if (!showCountrySelector) {
      return 'Enter phone number';
    }

    if (currentCountry) {
      const phoneExample = getNumberFormat();
      if (phoneExample) {
        return phoneExample;
      }
      return `${currentCountry.code || '+1'} 555 867 5309`;
    }

    return 'Enter phone number';
  }, [placeholder, showCountrySelector, currentCountry, getNumberFormat]);

  const handleCountryChange = (countryData: CountryOption | string) => {
    let country: CountryOption;

    if (typeof countryData === 'string') {
      // If we receive a string, we need to create a basic country object
      // This shouldn't normally happen with our CountrySelector, but handle it as fallback
      country = {
        value: countryData,
        label: countryData,
        code: currentCountry?.code || '+1',
        flag: currentCountry?.flag || '🇺🇸',
      };
    } else {
      country = countryData;
    }

    // Update internal country state
    setInternalCountry(country);

    if (onCountryChange) {
      onCountryChange(country);
    }

    // When country changes, revalidate existing phone number with new country
    // Don't clear the input - preserve user's entered number
    if (enableValidation && value) {
      const errorMessage = validatePhone(value);
      setValidationError(errorMessage);
    }

    // Ensure dropdown closes after selection
    setIsDropdownOpen(false);
  };

  // Handle country selector close - transfer focus to phone input and update state
  const handleCountrySelectorClose = () => {
    setIsDropdownOpen(false);
    setIsCountrySelectorFocused(false);
    // Small delay to ensure dropdown has closed before focusing input
    setTimeout(() => {
      if (phoneInputRef.current) {
        (phoneInputRef.current as HTMLInputElement).focus();
      }
    }, 50);
  };

  // Handle country selector focus - prevent event bubbling and track dropdown state
  const handleCountrySelectorFocus = (
    event: React.FocusEvent<HTMLSelectElement>,
  ) => {
    // Prevent the focus event from bubbling up to the main input field
    event.stopPropagation();

    // Set country selector as focused to trigger input border highlighting
    setIsCountrySelectorFocused(true);

    // Check if dropdown is opening by monitoring aria-expanded changes
    setTimeout(() => {
      if (countrySelectorRef.current) {
        const combobox = countrySelectorRef.current.querySelector(
          '[role="combobox"]',
        ) as HTMLElement;
        if (combobox && combobox.getAttribute('aria-expanded') === 'true') {
          setIsDropdownOpen(true);
        }
      }
    }, 100);
  };

  // Add click handler to detect dropdown opening
  const handleCountrySelectorClick = () => {
    // Check for dropdown state changes after click
    setTimeout(() => {
      if (countrySelectorRef.current) {
        const combobox = countrySelectorRef.current.querySelector(
          '[role="combobox"]',
        ) as HTMLElement;
        if (combobox && combobox.getAttribute('aria-expanded') === 'true') {
          setIsDropdownOpen(true);
        }
      }
    }, 50);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (restrictToNumbers) {
      // Allow numbers, spaces, parentheses, plus, and dash
      const allowedChars = /[0-9\s()\-+]/;

      // Prevent typing if the character is not allowed
      if (
        !allowedChars.test(event.key) &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(
          event.key,
        )
      ) {
        event.preventDefault();
        return;
      }

      // Prevent multiple plus signs
      if (event.key === '+') {
        const target = event.target as HTMLInputElement;
        const currentValue = target.value;
        const hasPlus = currentValue.includes('+');
        const cursorPosition = target.selectionStart;

        // Only allow plus at the beginning and only one plus sign total
        if (hasPlus || cursorPosition !== 0) {
          event.preventDefault();
        }
      }
    }
  };

  // Enhanced onChange handler that includes phone validation and restrictions
  const handleInputChange = (newValue: string) => {
    // Don't process changes if component is disabled
    if (disabled) {
      return;
    }

    let inputValue = newValue;

    // Restrict to numbers if enabled
    if (restrictToNumbers) {
      inputValue = extractNumbers(inputValue);
    }

    // Prevent input beyond valid length for the selected country (if enabled)
    if (enableLengthRestriction) {
      const maxLength = getMaxAllowedLength(inputValue, currentCountry);
      if (inputValue.length > maxLength) {
        inputValue = inputValue.substring(0, maxLength);
      }
    }

    // Validate in real-time as user types
    if (enableValidation) {
      const errorMessage = validatePhone(inputValue);
      setValidationError(errorMessage);
    }

    if (onChange) {
      onChange(inputValue);
    }
  };

  // Enhanced onBlur handler for validation
  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (enableValidation) {
      const inputValue = event.target.value;
      const errorMessage = validatePhone(inputValue);
      setValidationError(errorMessage);
    }
  };

  // If no country selector, use standard Input component
  if (!showCountrySelector) {
    return (
      <div ref={containerRef} className={stylesheet.phoneInputContainer}>
        <Input
          ref={phoneInputRef}
          id={inputId}
          name="phone"
          type={type}
          label={phoneNumberLabel}
          value={value}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onKeyDown={handleKeyDown}
          placeholder={dynamicPlaceholder}
          disabled={disabled}
          readOnly={readOnly}
          error={error || validationError}
          autocomplete="tel"
          inputMode="tel"
          {...props}
        />
      </div>
    );
  }

  // Create the country selector content for integration with Input
  const countrySelector = (
    <>
      <div
        tabIndex={0}
        ref={countrySelectorRef}
        className={stylesheet.countrySelectorWrapper}
        onClick={handleCountrySelectorClick}
      >
        <CountrySelector
          onChange={handleCountryChange}
          value={currentCountry.value}
          className={stylesheet.countrySelectorIntegrated}
          readOnly={readOnly}
          disabled={disabled}
          onClose={handleCountrySelectorClose}
          onFocus={handleCountrySelectorFocus}
        />
      </div>
      <span className={stylesheet.dialingCodeDisplay}>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {currentCountry?.code || '+1'}
      </span>
    </>
  );

  // Use Input component with integrated country selector
  return (
    <div
      ref={containerRef}
      className={cx(
        stylesheet.phoneInputContainer,
        props.className,
        isDropdownOpen && stylesheet.phoneInputContainerOpen,
        isCountrySelectorFocused && stylesheet.phoneInputContainerFocused,
      )}
    >
      <Input
        ref={phoneInputRef}
        id={inputId}
        name="phone"
        type={type}
        label={phoneNumberLabel}
        value={value}
        onChange={(newValue) => !isDropdownOpen && handleInputChange(newValue)}
        onBlur={handleInputBlur}
        onKeyDown={(e) => !isDropdownOpen && handleKeyDown(e)}
        placeholder={dynamicPlaceholder}
        disabled={disabled}
        readOnly={readOnly || isDropdownOpen}
        error={error || validationError}
        autocomplete="tel"
        inputMode="tel"
        prefixInlineSelect={countrySelector}
        {...props}
      />
    </div>
  );
};

// Set displayName for Storybook
InputPhone.displayName = 'InputPhone';
